// Lightweight frontend-only IPA approximator (pure JS, no native libs)
// Produces compact IPA (no spaces between phonemes) wrapped in slashes, e.g. /həloʊ/

// A small dictionary for high-frequency words to improve accuracy
const DICTIONARY = {
  hello: 'həˈloʊ',
  world: 'wɜːrld',
  apple: 'ˈæpəl',
  testing: 'ˈtɛstɪŋ',
  conversion: 'kənˈvɜːrʒən',
  ipa: 'aɪ piː eɪ',
  the: 'ðə',
  a: 'ə',
  an: 'æn',
  i: 'aɪ',
  you: 'juː',
  we: 'wiː',
  they: 'ðeɪ',
  he: 'hiː',
  she: 'ʃiː',
  it: 'ɪt',
  to: 'tuː',
  of: 'ɒv',
  and: 'ænd',
  is: 'ɪz',
  are: 'ɑːr'
};

// Basic grapheme -> IPA replacement rules (longest-first)
const RULES = [
  ['tion', 'ʃən'], ['ture', 'tʃər'], ['ough', 'ɔː'], ['eigh', 'eɪ'], ['igh', 'aɪ'],
  ['ph', 'f'], ['ch', 'tʃ'], ['sh', 'ʃ'], ['th', 'θ'], ['ng', 'ŋ'],
  ['ee', 'iː'], ['ea', 'iː'], ['oo', 'uː'], ['ow', 'aʊ'], ['oi', 'ɔɪ'], ['ai', 'eɪ'],
  ['au', 'ɔː'], ['qu', 'kw'], ['kn', 'n'], ['wr', 'r'], ['wh', 'w']
];

const sanitize = (s) => String(s || '').toLowerCase().replace(/[^a-z\s']/g, '').trim();

function applyRules(word) {
  if (!word) return '';
  let w = word;
  // Apply longest rules first
  for (const [pat, rep] of RULES) {
    const re = new RegExp(pat, 'g');
    w = w.replace(re, rep);
  }

  // Heuristics for remaining single letters (vowels and simple consonants)
  // handle common vowel sequences and endings
  w = w.replace(/([aeiou])e$/g, (m, a) => {
    // bake -> beɪk -> approximate: a -> eɪ when final silent e
    if (a === 'a') return 'eɪ';
    if (a === 'i') return 'aɪ';
    return 'ə';
  });

  // Replace standalone vowels conservatively
  w = w.replace(/a/g, 'æ');
  w = w.replace(/e/g, 'ɛ');
  w = w.replace(/i/g, 'ɪ');
  w = w.replace(/o/g, 'ɒ');
  w = w.replace(/u/g, 'ʌ');

  // simple consonant fallbacks
  w = w.replace(/c/g, 'k');
  w = w.replace(/g/g, 'g');

  // collapse duplicated IPA characters (if any accidental repeats)
  w = w.replace(/\s+/g, '');
  return w;
}

// Convert a full text line to spaced IPA wrapped in slashes
function textToIPAPure(text) {
  if (!text) return '';
  const cleaned = sanitize(text);
  if (!cleaned) return '';
  const tokens = cleaned.split(/\s+/).filter(Boolean);
  const parts = tokens.map(tok => {
    if (DICTIONARY[tok]) return DICTIONARY[tok].trim();
    return applyRules(tok);
  }).filter(Boolean);

  // Normalize spacing inside each part (dictionary entries may include spaces)
  const normalized = parts.map(p => p.replace(/\s+/g, ' ').trim());
  return normalized.length ? '/' + normalized.join(' ') + '/' : '';
}

export { textToIPAPure };
